import orderBy from "lodash/orderBy";

/**
 * Joins `locals.weeks` session exercise ids to the flat program exercise list (session id order preserved).
 */

const EMPTY_RESULT = Object.freeze({
  weeks: [],
  stats: Object.freeze({
    weekCount: 0,
    sessionCount: 0,
    totalResolvedExercises: 0,
    totalMissingRefs: 0,
  }),
});

/**
 * Builds a map of exercises by their _id.
 * @param {Object[]} exercises - The exercises to build the map from.
 * @returns {Map<string, Object>} A map of exercises by their _id.
 */
export function buildExerciseByIdMap(exercises) {
  const map = new Map();
  if (!Array.isArray(exercises)) {
    return map;
  }
  for (const ex of exercises) {
    if (ex?._id != null) {
      map.set(String(ex._id), ex);
    }
  }
  return map;
}

/**
 * Lists the sorted week indices from a weeks record.
 * @param {Object} weeksRecord - The weeks record to list the week indices from.
 * @returns {number[]} The sorted week indices.
 */
export function listSortedWeekIndices(weeksRecord) {
  if (!weeksRecord || typeof weeksRecord !== "object") {
    return [];
  }
  return Object.keys(weeksRecord)
    .map((k) => Number.parseInt(k, 10))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
}

/**
 * Resolves the exercises for a session.
 * @param {Object} session - The session to resolve the exercises for.
 * @param {Map<string, Object>} exerciseById - The map of exercises by their _id.
 * @returns {Object} The resolved exercises for the session.
 */
export function resolveSessionExercises(session, exerciseById) {
  const week = typeof session?.week === "number" ? session.week : 0;
  const order = typeof session?.order === "number" ? session.order : 0;
  const idList = Array.isArray(session?.exercises) ? session.exercises : [];

  const resolvedExercises = [];
  const missingExerciseIds = [];

  for (const rawId of idList) {
    const id = String(rawId);
    const doc = exerciseById.get(id);
    if (doc) {
      resolvedExercises.push(doc);
    } else {
      missingExerciseIds.push(id);
    }
  }

  return {
    week,
    order,
    resolvedExercises,
    missingExerciseIds,
  };
}

/**
 * Resolves the exercises for a week.
 * @param {Object[]} weekSessions - The week sessions to resolve the exercises for.
 * @param {Map<string, Object>} exerciseById - The map of exercises by their _id.
 * @returns {Object[]} The resolved exercises for the week.
 */
export function resolveWeekSessions(weekSessions, exerciseById) {
  if (!Array.isArray(weekSessions) || weekSessions.length === 0) {
    return [];
  }
  const ordered = orderBy(weekSessions, "order");
  return ordered.map((session) => resolveSessionExercises(session, exerciseById));
}

/**
 * Computes the statistics for a weeks record.
 * @param {Object[]} weeks - The weeks to compute the statistics for.
 * @returns {Object} The statistics for the weeks.
 */
function computeStats(weeks) {
  let sessionCount = 0;
  let totalResolvedExercises = 0;
  let totalMissingRefs = 0;
  for (const w of weeks) {
    for (const s of w.sessions) {
      sessionCount += 1;
      totalResolvedExercises += s.resolvedExercises.length;
      totalMissingRefs += s.missingExerciseIds.length;
    }
  }
  return {
    weekCount: weeks.length,
    sessionCount,
    totalResolvedExercises,
    totalMissingRefs,
  };
}

/**
 * Resolves the exercises for all program weeks.
 * @param {Object} weeksRecord - The weeks record to resolve the exercises for.
 * @param {Object[]} exercises - The exercises to resolve the exercises for.
 * @returns {Object} The resolved exercises for all program weeks.
 */
export function resolveAllProgramWeeksExercises(weeksRecord, exercises) {
  if (weeksRecord == null || typeof weeksRecord !== "object") {
    return EMPTY_RESULT;
  }

  const exerciseList = Array.isArray(exercises) ? exercises : [];
  const exerciseById = buildExerciseByIdMap(exerciseList);
  const weekIndices = listSortedWeekIndices(weeksRecord);

  if (weekIndices.length === 0) {
    return EMPTY_RESULT;
  }

  const weeks = [];

  for (const weekIndex of weekIndices) {
    const key = String(weekIndex);
    const weekSessions = weeksRecord[key];
    const sessions = resolveWeekSessions(weekSessions, exerciseById);
    weeks.push({
      weekIndex,
      sessions,
    });
  }

  const stats = computeStats(weeks);

  return {
    weeks,
    stats,
  };
}

/**
 * Compact exercise snapshot for local console inspection (Program Insights dev log).
 * @param {Object|null|undefined} ex
 * @returns {object|null}
 */
export function summarizeExerciseForProgramInsightsLog(ex) {
  if (ex?._id == null) {
    return null;
  }
  const rows = Array.isArray(ex.sets) ? ex.sets : [];
  const firstSetsSample = rows.slice(0, 3).map((row) => ({
    sets: row?.sets,
    reps: row?.reps,
    rpe: row?.rpe,
    load: row?.load,
    percentRepMax: row?.percentRepMax,
  }));
  return {
    id: String(ex._id),
    type: ex.type,
    primary: ex.primary,
    secondary: ex.secondary,
    exercise: ex.exercise,
    customName: ex.customName || undefined,
    musclePrimary: ex.primaryMuscleGroup ?? undefined,
    muscleSecondary: ex.secondaryMuscleGroup ?? undefined,
    setRows: rows.length,
    ...(firstSetsSample.length > 0 ? { firstSetsSample } : {}),
  };
}
